import { Form, Input, InputNumber, Modal, Select, Switch } from 'antd';
import { FC, useEffect, useMemo } from 'react';

import { useGetCustomerList } from '@/modules/customer/hooks/useGetCustomerList';
import { useGetProvinceList } from '@/hooks/useGetProvinceList';
import { useGetDistrictList } from '@/hooks/useGetDistrictList';
import { useGetwardList } from '@/hooks/useGetwardList';
import { useCreateFactory, useUpdateFactory } from '@/modules/factory/hooks/useFactoryApi';
import { FactoryModel } from '@/modules/factory/models';

interface IProps {
  readonly customerId: string;
  readonly initial?: FactoryModel;
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onSaved?: () => void;
}

const FactoryModal: FC<IProps> = ({ customerId, initial, visible, onClose, onSaved }) => {
  const [form] = Form.useForm<FactoryModel>();
  const create = useCreateFactory({ onSuccess: () => { onSaved?.(); onClose(); } });
  const update = useUpdateFactory({ onSuccess: () => { onSaved?.(); onClose(); } });

  // QC #31 — Customer dropdown inside the modal so users can pick a customer
  // even when the page-level filter is empty. Without it, clicking "Tạo nhà
  // máy" without first picking a customer in the filter sent customer_id=""
  // → BE 400 "Thiếu name hoặc customer_id".
  const { data: customers } = useGetCustomerList('page=1&limit=200');
  const customerOptions = useMemo(
    () =>
      ((customers as unknown as { rows?: Array<{ id?: string; name: string }> })?.rows ?? [])
        .filter((c) => c.id)
        .map((c) => ({ label: c.name, value: c.id! })),
    [customers]
  );

  const watchProvinceId = Form.useWatch('province_id', form);
  const watchDistrictId = Form.useWatch('district_id', form);

  const { data: provinceList } = useGetProvinceList();
  const { data: districtList } = useGetDistrictList(watchProvinceId || '', { enabled: !!watchProvinceId });
  const { data: wardList } = useGetwardList(watchDistrictId || '', { enabled: !!watchDistrictId });

  const provinceOptions = useMemo(() => provinceList?.map(p => ({ label: p.name, value: p.code.toString() })) || [], [provinceList]);
  const districtOptions = useMemo(() => districtList?.map(d => ({ label: d.name, value: d.code.toString() })) || [], [districtList]);
  const wardOptions = useMemo(() => wardList?.map((w: any) => ({ label: w.name, value: w.code.toString() })) || [], [wardList]);


  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initial) form.setFieldsValue(initial);
      else form.setFieldsValue({
        baseline_headcount: 0,
        customer_id: customerId,
        is_active: true,
        name: '',
        address: '',
        province_id: undefined,
        district_id: undefined,
        ward_id: undefined,
        payroll_cycle_start_day: 1,
        shift_config: '{}',
        wait_to_resigned_days: 30,
      });
    }
  }, [visible, initial, customerId, form]);

  async function handleOk() {
    const values = await form.validateFields();
    // Modal's customer_id wins over the prop — user may have picked a different
    // one in the in-modal Select.
    const payload = { ...values, customer_id: values.customer_id || customerId };

    if (!payload.latitude) delete payload.latitude;
    if (!payload.longitude) delete payload.longitude;
    if (!payload.province_id) delete payload.province_id;
    if (!payload.district_id) delete payload.district_id;
    if (!payload.ward_id) delete payload.ward_id;

    payload.baseline_headcount = payload.baseline_headcount || 0;
    payload.wait_to_resigned_days = payload.wait_to_resigned_days || 30;
    payload.payroll_cycle_start_day = payload.payroll_cycle_start_day || 1;

    if (initial?.id) update.mutate({ ...payload, id: initial.id });
    else create.mutate(payload);
  }

  return (
    <Modal
      cancelText="Huỷ"
      confirmLoading={create.isLoading || update.isLoading}
      okText={initial?.id ? 'Cập nhật' : 'Tạo nhà máy'}
      onCancel={onClose}
      onOk={handleOk}
      open={visible}
      title={initial?.id ? 'Chỉnh sửa nhà máy' : 'Tạo nhà máy mới'}
      width={720}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Khách hàng"
          name="customer_id"
          rules={[{ message: 'Vui lòng chọn khách hàng', required: true }]}
        >
          <Select
            disabled={!!initial?.id}
            optionFilterProp="label"
            options={customerOptions}
            placeholder="Chọn khách hàng"
            showSearch
          />
        </Form.Item>

        <Form.Item
          label="Tên nhà máy"
          name="name"
          rules={[{ message: 'Bắt buộc', required: true }]}
        >
          <Input placeholder="Nhà máy A — Quế Võ" />
        </Form.Item>

        <div className="row">
          <div className="col-md-4">
            <Form.Item label="Tỉnh/ Thành phố" name="province_id">
              <Select options={provinceOptions} showSearch optionFilterProp="label" placeholder="Chọn tỉnh/thành" onChange={() => form.setFieldsValue({ district_id: undefined, ward_id: undefined })} allowClear />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label="Quận/ Huyện" name="district_id">
              <Select options={districtOptions} showSearch optionFilterProp="label" placeholder="Chọn quận/huyện" onChange={() => form.setFieldsValue({ ward_id: undefined })} disabled={!watchProvinceId} allowClear />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item label="Phường/ Xã" name="ward_id">
              <Select options={wardOptions} showSearch optionFilterProp="label" placeholder="Chọn phường/xã" disabled={!watchDistrictId} allowClear />
            </Form.Item>
          </div>
        </div>

        <Form.Item label="Địa chỉ chi tiết" name="address">
          <Input placeholder="Số nhà / đường / khu công nghiệp..." />
        </Form.Item>

        <div className="row">
          <div className="col-md-6">
            <Form.Item
              extra="V1.4 A11.5 — sĩ số KH yêu cầu duy trì liên tục."
              label="Baseline headcount"
              name="baseline_headcount"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <Form.Item
              extra="V1.4 B5 — Số ngày NLĐ ở CHỜ VIỆC trước khi auto chuyển ĐÃ NGHỈ."
              label="Wait-to-resigned (ngày)"
              name="wait_to_resigned_days"
              rules={[{ required: true }]}
            >
              <InputNumber max={365} min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <Form.Item label="Ngày bắt đầu kỳ lương" name="payroll_cycle_start_day" rules={[{ message: 'Bắt buộc', required: true }]}>
              <InputNumber max={28} min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <Form.Item label="Đang hoạt động" name="is_active" valuePropName="checked">
              <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
            </Form.Item>
          </div>
        </div>

        <Form.Item
          extra="JSONB: shifts + overtimeRules + multipliers. Để '{}' nếu chưa cấu hình."
          label="Cấu hình ca làm việc (JSON)"
          name="shift_config"
        >
          <Input.TextArea autoSize={{ maxRows: 8, minRows: 3 }} />
        </Form.Item>

        <div className="row">
          <div className="col-md-6">
            <Form.Item label="Vĩ độ (lat)" name="latitude">
              <Input placeholder="21.0285" />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <Form.Item label="Kinh độ (lng)" name="longitude">
              <Input placeholder="105.8542" />
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export { FactoryModal };
